import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
  FullScreenOverlayBody,
  FullScreenOverlayContent,
  FullScreenOverlayHeader
} from '@bibliocommons/base-full-screen-overlay';
import { libraryShape, subscriptionShape } from '@bibliocommons/bc-prop-types';
import { TableStencil } from '@bibliocommons/table';
import { bindAll } from '@bibliocommons/utils-react';

import ILSOfflineMessage from 'app/components/bibs/shared/ILSOfflineMessage';
import { StackMapDisplayContainer } from 'app/components/bibs/shared/StackMap/StackMapDisplay';
import UniversalBibBriefContainer from 'app/components/bibs/shared/UniversalBibBrief/UniversalBibBriefContainer';
import Tabs from 'app/components/shared/Tabs';
import Subscriptions from './Subscriptions';
import ItemAvailabilityTable from './ItemAvailabilityTable';

import './AvailabilityDetails.scss';

const AVAILABILITY_TABS = {
  CIRCULATION: 'circulation',
  SUBSCRIPTIONS: 'subscription'
};

export class AvailabilityDetails extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  state = {
    stackMapOpen: false
  };

  handleStackMapOpen = () => {
    this.setState({ stackMapOpen: true });
  };

  handleStackMapClose = () => {
    this.setState({ stackMapOpen: false });
  };

  trackRequestFormClick({ itemId, linkText }) {
    const { metadataId, trackRequestExternalPlace } = this.props;

    trackRequestExternalPlace({
      bibId: metadataId,
      metadata: {
        metadata_category_type: 'holdings_resource_item_id',
        metadata_value: itemId
      },
      ui: {
        ui_component_label: linkText,
        ui_component_type: 'link-text'
      }
    });
  }

  renderCirculation() {
    const { bibItems, currentLibrary, probes } = this.props;

    return (
      <ItemAvailabilityTable
        bibItems={bibItems}
        currentLibrary={currentLibrary}
        handleStackMapOpen={this.handleStackMapOpen}
        probes={probes}
        trackRequestFormClick={this.trackRequestFormClick}
      />
    );
  }

  renderSubscriptions() {
    const { subscriptions } = this.props;
    return <Subscriptions subscriptions={subscriptions} />;
  }

  renderTabs() {
    const { intl } = this.props;

    const newTabOptions = [
      {
        id: AVAILABILITY_TABS.SUBSCRIPTIONS,
        label: intl.formatMessage({ id: `availability_modal_tab_${AVAILABILITY_TABS.SUBSCRIPTIONS}` }),
        renderPanelBody: this.renderSubscriptions
      },
      {
        id: AVAILABILITY_TABS.CIRCULATION,
        label: intl.formatMessage({ id: `availability_modal_tab_${AVAILABILITY_TABS.CIRCULATION}` }),
        renderPanelBody: this.renderCirculation
      }
    ];

    return <Tabs ariaLabel={intl.formatMessage({ id: 'availability_details' })} options={newTabOptions} />;
  }

  renderMainContent() {
    if (this.props.isFetching) return <TableStencil />;
    if (this.state.stackMapOpen) return <StackMapDisplayContainer />;
    if (this.props.bibItems.size > 0 && this.props.subscriptions.size > 0) return this.renderTabs();
    if (this.props.bibItems.size > 0) return this.renderCirculation();
    if (this.props.subscriptions.size > 0) return this.renderSubscriptions();
    return null;
  }

  render() {
    const { availabilityError, isFetching, metadataId } = this.props;

    const title = this.state.stackMapOpen ? (
      <FormattedMessage id="find_item" />
    ) : (
      <FormattedMessage id="availability_details" />
    );

    return (
      <FullScreenOverlayBody className="cp-availability-details">
        <FullScreenOverlayHeader title={title} />
        <FullScreenOverlayContent>
          <UniversalBibBriefContainer metadataId={metadataId} />
          {!isFetching && <ILSOfflineMessage availabilityError={availabilityError} />}
          {this.renderMainContent()}
        </FullScreenOverlayContent>
      </FullScreenOverlayBody>
    );
  }
}

AvailabilityDetails.propTypes = {
  availabilityError: PropTypes.string,
  bibItems: ImmutablePropTypes.list.isRequired,
  currentLibrary: libraryShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  metadataId: PropTypes.string.isRequired,
  probes: ImmutablePropTypes.list.isRequired,
  subscriptions: ImmutablePropTypes.listOf(subscriptionShape).isRequired,
  trackRequestExternalPlace: PropTypes.func.isRequired
};

export default injectIntl(AvailabilityDetails);
